import classNames from "classnames"
import Image from "next/image"
import React from "react"

import { Analytics } from "@bounce/analytics"
import { useLocale, useTranslation } from "@bounce/i18n"
import { getStaticMap } from "@bounce/util"
import type { Coordinates } from "@bounce/util"

import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { config } from "@/config"
import { InteractionEvents } from "@/libs/analytics/events"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

export type StaticMapProps = {
  coordinates: Coordinates
  zoom?: number
  className?: string
  nearestStores: NearbyStorageSpot[]
  searchUrl: string
  query: string
}

export const StaticMap = ({ coordinates, zoom = 13, nearestStores, className, searchUrl, query }: StaticMapProps) => {
  const { t } = useTranslation()
  const { language } = useLocale()

  const staticMapImage = getStaticMap({
    width: 1280,
    height: 428,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    zoom,
    language,
    markers: nearestStores.map(store => ({
      latitude: store.coordinates.latitude,
      longitude: store.coordinates.longitude,
    })),
    scale: 1,
    markersScale: 1,
    mapId: "baf29dad78527709",
    key: config.googleMaps.key,
  })

  return (
    <SafeLink
      href={searchUrl}
      onClick={() => Analytics.track(InteractionEvents.StaticMapPressed, { place: query })}
      data-chromatic="ignore"
      className={classNames("relative overflow-hidden", className)}>
      <Image
        src={staticMapImage}
        width={1280}
        height={428}
        priority
        loading="eager"
        alt={t("cmp.cityMap.mapImgAltV1", "View stores in {QUERY} on Google Maps.", { QUERY: query })}
        className="size-full h-[260px] object-cover object-center md:h-[428px]"
      />
    </SafeLink>
  )
}
